import React, { useState } from 'react';
import { ExpandLess, ExpandMore, Person, Schedule } from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';

import GlassCard from '@/components/ui/GlassCard';

export interface ContractClauseComparison {
  clauseName: string;
  category: 'offer' | 'contingency' | 'timeline' | 'property' | 'other';
  sellerInput: string | number | null;
  agentInput: string | number | null;
  buyerInput: string | number | null;
  adminNotes: string;
  status: 'pending' | 'agreed' | 'disputed' | 'modified';
  lastUpdatedBy: 'seller' | 'agent' | 'buyer' | 'admin';
  lastUpdatedAt: Date;
  negotiationHistory: Array<{
    party: string;
    value: any;
    timestamp: Date;
    notes: string;
  }>;
}

interface ContractComparisonTableProps {
  clauses: ContractClauseComparison[];
  onClauseClick?: (clause: ContractClauseComparison) => void;
}

const categoryLabel = (category: string): string => {
  switch (category) {
    case 'offer':
      return 'Offer';
    case 'contingency':
      return 'Contingencies';
    case 'timeline':
      return 'Timeline';
    case 'property':
      return 'Property';
    case 'other':
      return 'Other';
    default:
      return category;
  }
};

const ContractComparisonTable: React.FC<ContractComparisonTableProps> = ({
  clauses,
  onClauseClick,
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const headBg = alpha(theme.palette.primary.main, 0.08);
  const rowOdd = theme.palette.background.paper;
  const rowEven = alpha(theme.palette.action.hover, 0.04);
  const groupBg = alpha(theme.palette.primary.main, 0.06);

  const toggleRowExpansion = (clauseName: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(clauseName)) {
      newExpanded.delete(clauseName);
    } else {
      newExpanded.add(clauseName);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agreed':
        return 'success';
      case 'disputed':
        return 'error';
      case 'modified':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'offer':
        return 'primary';
      case 'contingency':
        return 'secondary';
      case 'timeline':
        return 'info';
      case 'property':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return format(value, 'MMM dd, yyyy');
    return String(value);
  };

  const groupedClauses = clauses.reduce((acc, clause) => {
    if (!acc[clause.category]) {
      acc[clause.category] = [];
    }
    acc[clause.category].push(clause);
    return acc;
  }, {} as Record<string, ContractClauseComparison[]>);

  return (
    <GlassCard
      variant="medium"
      sx={{
        color: 'text.primary',
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          gutterBottom
          color="text.primary"
        >
          Contract Comparison
        </Typography>

        <TableContainer
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Clause/Field
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Seller
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Agent
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Buyer
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Admin Notes
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: headBg,
                    color: 'text.primary',
                    fontWeight: 700,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedClauses).map(
                ([category, categoryClauses]) => (
                  <React.Fragment key={category}>
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          backgroundColor: groupBg,
                          color: 'text.primary',
                          fontWeight: 700,
                          py: 1.5,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Chip
                          label={categoryLabel(category)}
                          color={getCategoryColor(category)}
                          size="small"
                          sx={{ mr: 2, fontWeight: 600 }}
                        />
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          {categoryClauses.length}{' '}
                          {categoryClauses.length === 1 ? 'clause' : 'clauses'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {categoryClauses.map((clause, rowIdx) => (
                      <React.Fragment key={clause.clauseName}>
                        <TableRow
                          hover
                          onClick={() => onClauseClick?.(clause)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              rowIdx % 2 === 0 ? rowOdd : rowEven,
                            '&:last-of-type td': {
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                          }}
                        >
                          <TableCell
                            sx={{ color: 'text.primary', fontWeight: 500 }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight={600}>
                                {clause.clauseName}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-expanded={expandedRows.has(
                                  clause.clauseName
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpansion(clause.clauseName);
                                }}
                              >
                                {expandedRows.has(clause.clauseName) ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {formatValue(clause.sellerInput)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {formatValue(clause.agentInput)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {formatValue(clause.buyerInput)}
                          </TableCell>
                          <TableCell
                            sx={{ color: 'text.secondary', maxWidth: 220 }}
                          >
                            {clause.adminNotes || 'No notes'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={clause.status}
                              color={getStatusColor(clause.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                            >
                              <Chip
                                icon={<Person />}
                                label={clause.lastUpdatedBy}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                              <Chip
                                icon={<Schedule />}
                                label={format(clause.lastUpdatedAt, 'MMM dd')}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                            <Collapse
                              in={expandedRows.has(clause.clauseName)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.04
                                  ),
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  fontWeight={600}
                                  color="text.primary"
                                >
                                  Negotiation History
                                </Typography>
                                {clause.negotiationHistory.length > 0 ? (
                                  <Box sx={{ pl: 1 }}>
                                    {clause.negotiationHistory.map(
                                      (entry, index) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            mb: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          <Chip
                                            icon={<Person />}
                                            label={entry.party}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ textTransform: 'capitalize' }}
                                          />
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {formatValue(entry.value)}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.disabled"
                                          >
                                            {format(
                                              entry.timestamp,
                                              'MMM dd, yyyy HH:mm'
                                            )}
                                          </Typography>
                                          {entry.notes && (
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              — {entry.notes}
                                            </Typography>
                                          )}
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    pl={1}
                                  >
                                    No negotiation history available
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </GlassCard>
  );
};

export default ContractComparisonTable;
